import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

interface VisionData {
  number: string;
  title: string;
  desc: string;
}

interface ValueData {
  title: string;
  desc: string;
}

const visionData: VisionData[] = [
  { number: '01', title: '통합 데이터 플랫폼', desc: '고해상도 디지털 슬라이드, 임상 정보, 면역조직화학검사, NGS 유전체 데이터를 하나의 플랫폼에 통합한 세계 최초의 소아 희귀질환 병리 아틀라스를 구축한다.' },
  { number: '02', title: '정밀 진단 중앙센터', desc: 'NGS 기반 분자 진단을 통해 진단이 어려운 환아들에게 최종 확진의 기회를 제공하고 전국 의료기관에 전문 자문 서비스를 수행한다.' },
  { number: '03', title: '차세대 전문인력 양성', desc: '디지털화된 전문 지식과 방대한 증례 데이터를 바탕으로 소아병리 분야의 차세대 리더를 육성하는 교육 플랫폼이 된다.' },
  { number: '04', title: '글로벌 오픈 데이터베이스', desc: '전 세계 의료 전문가들이 자유롭게 접근하고 활용할 수 있는 오픈 소스로 글로벌 희귀질환 연구 발전에 기여한다.' },
];

const valueData: ValueData[] = [
  { title: '환자중심', desc: '희귀질환 환아와 가족에게 정확한 진단과 최적의 치료 정보를 제공하여 실질적 의료 혜택을 창출한다.' },
  { title: '전문성', desc: '다기관 소아병리 전문가 그룹의 협력과 검증을 통해 세계 최고 수준의 데이터 품질을 유지한다.' },
  { title: '개방성', desc: '오픈 소스 정신을 바탕으로 전 세계 의료진과 데이터를 공유하며 글로벌 의학 발전에 기여한다.' },
  { title: '혁신추구', desc: 'AI와 빅데이터 기술을 접목하여 차세대 디지털 병리 진단 시스템의 토대를 구축한다.' },
  { title: '인재양성', desc: '풍부한 디지털 콘텐츠와 교육 프로그램을 통해 소아병리 분야의 차세대 전문가를 육성한다.' },
  { title: '사회공헌', desc: '희귀질환 진단 사각지대를 해소하고 의료 형평성 향상을 통해 국민 건강 증진에 기여한다.' },
];

const Mission: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions: IntersectionObserverInit = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, observerOptions);

    if (containerRef.current) {
      const elements = containerRef.current.querySelectorAll('.fade-in');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Wrapper ref={containerRef}>
      <MissionSection>
        <div className="container">
          <SectionTitle className="fade-in">미션</SectionTitle>
          <MissionText className="fade-in">
            고해상도 디지털 병리 통합 플랫폼을 통하여<br />
            소아 희귀질환 환아들이 정확한 진단과<br />
            최적의 치료를 받을 수 있도록 한다.
          </MissionText>
          <MissionVisual className="fade-in">
            <TriangleShape />
            <MissionCirclesLayout>
              <div className="circle-top">
                <MissionCircle>진단</MissionCircle>
              </div>
              <div className="circle-bottom">
                <MissionCircle>연구</MissionCircle>
                <MissionCircle>교육</MissionCircle>
              </div>
            </MissionCirclesLayout>
          </MissionVisual>
        </div>
      </MissionSection>

      <VisionSection>
        <div className="container">
          <SectionTitle className="fade-in">비전</SectionTitle>
          <VisionMain className="fade-in">
            임상·병리·유전 정보가 통합된<br />
            세계 최초의 소아 희귀질환 병리 아틀라스
          </VisionMain>
          <VisionGrid>
            {visionData.map((item, index) => (
              <VisionItemCard key={index} className="fade-in">
                <div className="vision-number">{item.number}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </VisionItemCard>
            ))}
          </VisionGrid>
        </div>
      </VisionSection>

      <ValuesSection>
        <div className="container">
          <SectionTitle className="fade-in">핵심가치</SectionTitle>
          <ValuesGrid>
            {valueData.map((item, index) => (
              <ValueItemCard key={index} className="fade-in">
                <div className="value-title">{item.title}</div>
                <p className="value-desc">{item.desc}</p>
              </ValueItemCard>
            ))}
          </ValuesGrid>
        </div>
      </ValuesSection>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  color: #333;
  overflow-x: hidden;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .fade-in {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
    &.active {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: #2c5aa0;
  margin-bottom: 40px;
  font-weight: 600;
  letter-spacing: -0.5px;
  text-align: center;
`;

const MissionSection = styled.section`
  padding: 100px 0;
`;

const MissionText = styled.p`
  font-size: 2rem;
  line-height: 1.8;
  color: #333;
  font-weight: 400;
  max-width: 900px;
  margin: 0 auto 80px;
  letter-spacing: -0.5px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const MissionVisual = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 60px 20px;
  position: relative;
`;

const TriangleShape = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  height: 400px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 200px solid transparent;
    border-right: 200px solid transparent;
    border-bottom: 346px solid rgba(44, 90, 160, 0.1);
  }

  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
    &::before {
      border-left: 150px solid transparent;
      border-right: 150px solid transparent;
      border-bottom: 260px solid rgba(44, 90, 160, 0.1);
    }
  }
`;

const MissionCirclesLayout = styled.div`
  position: relative;
  z-index: 10;
  .circle-top {
    display: flex;
    justify-content: center;
    margin-bottom: 100px;
  }
  .circle-bottom {
    display: flex;
    justify-content: center;
    gap: 200px;
    @media (max-width: 768px) {
      gap: 80px;
    }
  }
`;

const MissionCircle = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: white;
  border: 4px solid #2c5aa0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c5aa0;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(44, 90, 160, 0.2);
  cursor: default;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -8px; left: -8px; right: -8px; bottom: -8px;
    border-radius: 50%;
    border: 2px solid rgba(44, 90, 160, 0.2);
    transition: all 0.3s ease;
  }

  &:hover {
    background: #2c5aa0;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 10px 30px rgba(44, 90, 160, 0.35);
    &::before {
      border-color: #2c5aa0;
      top: -12px; left: -12px; right: -12px; bottom: -12px;
    }
  }

  @media (max-width: 768px) {
    width: 110px;
    height: 110px;
    font-size: 1rem;
  }
`;

const VisionSection = styled.section`
  padding: 100px 0;
  background: #f8f9fa;
`;

const VisionMain = styled.p`
  text-align: center;
  font-size: 2rem;
  line-height: 1.8;
  color: #333;
  font-weight: 400;
  margin-bottom: 80px;
  letter-spacing: -0.5px;
  @media (max-width: 768px) { font-size: 1.4rem; }
`;

const VisionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const VisionItemCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(0,0,0,0.12);
    transform: translateY(-5px);
  }

  .vision-number {
    font-size: 2.5rem;
    color: #2c5aa0;
    font-weight: 300;
    margin-bottom: 20px;
    font-family: 'Georgia', serif;
  }

  h3 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 20px;
    font-weight: 600;
  }

  p {
    font-size: 1.05rem;
    line-height: 1.8;
    color: #666;
  }
`;

const ValuesSection = styled.section`
  padding: 100px 0;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 50px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueItemCard = styled.div`
  text-align: center;
  .value-title {
    font-size: 1.5rem;
    color: #2c5aa0;
    font-weight: 600;
    margin-bottom: 20px;
  }
  .value-desc {
    font-size: 1.05rem;
    line-height: 1.8;
    color: #666;
  }
`;

export default Mission;